import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray } from 'redux-form';
import { Row, Col } from 'react-flexbox-grid';

import _get from 'lodash/get';
import _find from 'lodash/find';

import { entityType, featuresType } from 'types';

import TrashTooltip from 'components/TrashTooltip';

import { FormGroupSelect, FormGroupField } from 'components/Form';
import FormGroupAutoCompleteField from 'components/AutoCompleteMenuWrapper';
import EntitySpan from './components/EntitySpan';

import { ACTION_TYPE, FEATURE_VALUE_TYPE } from '../../../../constants';

import '../../style.scss';

const DEFAULT_SOURCE = {
  source_entity_feature: null,
  source_matching_pattern_element: null,
};

/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable react/destructuring-assignment */

export default class Mark extends Component {
  static propTypes = {
    change: PropTypes.func.isRequired,
    field: PropTypes.string.isRequired,
    matchingPatterns: PropTypes.arrayOf(entityType).isRequired,
    matchingPatternsWithoutAnyAndW: PropTypes.arrayOf(entityType).isRequired,
    type: PropTypes.string.isRequired,
    untouch: PropTypes.func.isRequired,
    onCreateEntity: PropTypes.func.isRequired,
    allEntities: PropTypes.arrayOf(entityType),

    allFeatures: PropTypes.arrayOf(featuresType),
    isArchived: PropTypes.bool,

    // REDUX-FORM:
    isEntityArchived: PropTypes.bool,
    isLoading: PropTypes.bool,
    /* eslint-disable-next-line react/forbid-prop-types */
    values: PropTypes.object,
    onSearchEntity: PropTypes.func,
  };

  static defaultProps = {
    onSearchEntity: () => {},
  };

  getFeatureValuesTemplates = type => {
    let featureValuesTemplates = [
      {
        label: 'Explicit',
        value: FEATURE_VALUE_TYPE.EXPLICIT,
      },
      {
        label: 'Dynamic',
        value: FEATURE_VALUE_TYPE.DYNAMIC,
      },
    ];

    // Concatenation & Cover Text option should be available only if the feature type is String
    if (type === 'String') {
      featureValuesTemplates = [
        ...featureValuesTemplates,
        {
          label: 'Concatenation',
          value: FEATURE_VALUE_TYPE.CONCATENATION,
        },
        {
          label: 'Cover Text',
          value: FEATURE_VALUE_TYPE.COVER_TEXT,
        },
      ];
    }

    return featureValuesTemplates;
  };

  createChangeValueTypeHandler = field => ({ target }) => {
    // Resets dependent fields once the root field is changed
    const explicitField = `${field}.explicit_value`;

    this.props.change(explicitField, null);
    this.props.untouch(explicitField);

    this.props.change(`${field}.dynamic_value`, null);
    this.props.change(
      `${field}.concatenation_value`,
      target.value === FEATURE_VALUE_TYPE.CONCATENATION
        ? [DEFAULT_SOURCE]
        : null,
    );
  };

  createChangeSourceEntityHandler = field => () => {
    // Resets dependent fields once the root field is changed
    this.props.change(`${field}.dynamic_value.source_entity_feature`, null);
  };

  createAddConcatHandler = fields => () => {
    fields.push(DEFAULT_SOURCE);
  };

  createRemoveConcatHandler = (fields, index) => () => {
    fields.remove(index);
  };

  handleMarkActionEntityChange = entity => {
    if (entity && entity.features) {
      this.props.change(
        `${this.props.field}.${this.props.type}.feature_values`,
        entity.features.map(feature => ({
          feature,
          value_type: '',
          dynamic_value: null,
          explicit_value: null,
          concatenation_value: null,
        })),
      );
    }
  };

  handleClickCreateEntity = () => {
    this.props.onCreateEntity(`${this.props.field}.${this.props.type}`);
  };

  handleSearchEntity = (searchTerm = '') => {
    this.props.onSearchEntity(searchTerm, false);
  };

  renderConcat = ({ fields }) => {
    const {
      matchingPatternsWithoutAnyAndW,
      isArchived,
      isEntityArchived,
    } = this.props;

    return (
      <Col xs>
        <Row>
          <Col xs={12}>
            {fields.map((field, index) => {
              const { source_matching_pattern_element } = fields.get(index);
              return (
                <Row key={field} styleName="item">
                  <Col xs={6}>
                    <Field
                      itemWithIndex
                      component={FormGroupAutoCompleteField}
                      data-test="select-source-matching-pattern"
                      isDisabled={isArchived || isEntityArchived}
                      items={matchingPatternsWithoutAnyAndW}
                      name={`${field}.source_matching_pattern_element`}
                      placeholder="Select Source Entity"
                    />
                  </Col>

                  <Col xs={6}>
                    <Field
                      component={FormGroupAutoCompleteField}
                      data-test="select-source-entity-feature"
                      isDisabled={isArchived || isEntityArchived}
                      items={_get(
                        source_matching_pattern_element,
                        'features',
                        [],
                      )}
                      name={`${field}.source_entity_feature`}
                      placeholder="Select Feature"
                    />
                  </Col>

                  {fields.length > 1 && (
                    <Col xs={0}>
                      <TrashTooltip
                        styleName="trash"
                        text="Delete"
                        onDelete={this.createRemoveConcatHandler(fields, index)}
                      />
                    </Col>
                  )}
                </Row>
              );
            })}
          </Col>
        </Row>

        <Row>
          <Col xs>
            <button
              data-test="add-concatenation-value"
              styleName="add"
              type="button"
              onClick={this.createAddConcatHandler(fields)}
            >
              Add Value
            </button>
          </Col>
        </Row>
      </Col>
    );
  };

  renderExplicit = (field, { feature_type }, isDisabled) => {
    const feature = _find(this.props.allFeatures, {
      name: feature_type,
    });

    if (!Array.isArray(feature.available_values)) {
      return (
        <Field
          component={FormGroupField}
          data-test="explicit-text"
          disabled={isDisabled}
          name={`${field}.explicit_value`}
          placeholder="Enter Text"
          type="text"
        />
      );
    }
    return (
      <Field
        component={FormGroupSelect}
        data-test="explicit-values"
        disabled={isDisabled}
        name={`${field}.explicit_value`}
        options={feature.available_values.map(value => ({
          label: value.toString(), // because of boolean case
          value,
        }))}
        placeholder="Select Value"
      />
    );
  };

  renderDynamic = (field, values, isDisabled) => {
    const { matchingPatternsWithoutAnyAndW } = this.props;

    return [
      <Col key="0" xs={3}>
        <Field
          itemWithIndex
          component={FormGroupAutoCompleteField}
          data-test="select-source-entity"
          isDisabled={isDisabled}
          items={matchingPatternsWithoutAnyAndW}
          name={`${field}.dynamic_value.source_matching_pattern_element`}
          placeholder="Select Source Entity"
          onChange={this.createChangeSourceEntityHandler(field)}
        />
      </Col>,
      <Col key="1" xs={3}>
        <Field
          component={FormGroupAutoCompleteField}
          data-test="select-source-feature"
          isDisabled={isDisabled}
          items={_get(
            values,
            'dynamic_value.source_matching_pattern_element.features',
            [],
          )}
          name={`${field}.dynamic_value.source_entity_feature`}
          placeholder="Select Feature"
        />
      </Col>,
    ];
  };

  renderFeatures = ({ fields }) => (
    <Row>
      {fields.map((field, index) => {
        const { value_type, feature, ...values } = fields.get(index);
        const { isArchived, isEntityArchived } = this.props;
        const isDisabled = isArchived || isEntityArchived;

        if (!feature) {
          return null;
        }

        return (
          <Col key={field} xs={12}>
            <Row>
              <Col xs={2} xsOffset={1}>
                <div styleName="property-name">{feature.name}</div>
              </Col>

              <Col xs={3}>
                <Field
                  placeholderSelectable
                  component={FormGroupSelect}
                  data-test="select-type"
                  disabled={isDisabled}
                  name={`${field}.value_type`}
                  options={this.getFeatureValuesTemplates(feature.feature_type)}
                  placeholder="Select Type"
                  onChange={this.createChangeValueTypeHandler(field)}
                />
              </Col>

              {value_type === FEATURE_VALUE_TYPE.EXPLICIT && (
                <Col xs={3}>
                  {this.renderExplicit(field, feature, isDisabled)}
                </Col>
              )}

              {value_type === FEATURE_VALUE_TYPE.DYNAMIC &&
                this.renderDynamic(field, values, isDisabled)}

              {value_type === FEATURE_VALUE_TYPE.CONCATENATION && (
                <FieldArray
                  component={this.renderConcat}
                  name={`${field}.concatenation_value`}
                />
              )}
            </Row>
          </Col>
        );
      })}
    </Row>
  );

  render() {
    const {
      values,
      type,
      field,
      allEntities,
      matchingPatterns,
      matchingPatternsWithoutAnyAndW,
      isArchived,
      isLoading,
      isEntityArchived,
    } = this.props;
    const name = `${field}.${type}.${
      type === ACTION_TYPE.MARK ? 'entity' : 'matching_pattern_element'
    }`;

    return (
      <Row>
        <Col xs={12}>
          <Row>
            <Col styleName="field-group-type" xs={2}>
              {type === ACTION_TYPE.MARK ? 'Mark' : 'Set Feature Value'}
            </Col>

            <Col xs={4}>
              <Field
                component={FormGroupAutoCompleteField}
                data-test="select-entity"
                isArchived={isEntityArchived}
                isDisabled={isArchived}
                isLoading={isLoading}
                items={
                  type === ACTION_TYPE.MARK
                    ? allEntities
                    : matchingPatternsWithoutAnyAndW
                }
                itemWithIndex={type === ACTION_TYPE.FILL}
                name={name}
                placeholder="Select Entity"
                withAddOption={
                  type === ACTION_TYPE.MARK ? 'Create New Entity' : null
                }
                onChange={this.handleMarkActionEntityChange}
                onChangeValue={this.handleSearchEntity}
                onClickAddOption={this.handleClickCreateEntity}
                onOpen={this.handleSearchEntity}
              />
            </Col>

            {type === ACTION_TYPE.MARK && (
              <EntitySpan
                formFieldsPath={`${field}.${type}`}
                fromFieldPayload={_get(
                  values,
                  'mark_action.matching_pattern_element_from',
                  null,
                )}
                matchingPatterns={matchingPatterns}
              />
            )}
          </Row>

          <FieldArray
            component={this.renderFeatures}
            name={`${field}.${type}.feature_values`}
          />
        </Col>
      </Row>
    );
  }
}
